import { CheckIcon, CircleArrowRightIcon } from "lucide-react";

import {
  getSubmissibilityColor,
  getSubmissibilityProgress,
  getSubmissibilitySectionKey,
  getSubmissibilitySummaryText,
  groupIncorrectFields,
} from "simplydo/core/submissibility";
import type { SubmissibilityIncorrectField } from "simplydo/core/submissibility";
import { Divider } from "simplydo/react/ui/divider";
import { Icon } from "simplydo/react/ui/icon";
import { List, ListItem } from "simplydo/react/ui/list";

import util from "utils/utils";

import type {
  IdeaSidebarSection,
  IdeaWithSubmissibilityStatus,
  UserMe,
} from "components/ideas/types";
import CircularProgress from "components/lib/CircularProgress";
import type { SideNavItemProps } from "components/lib/SideNav/SideNavComponent";

type CustomSectionEntryProps = {
  user: UserMe | null;
  idea: IdeaWithSubmissibilityStatus;
  section: IdeaSidebarSection;
  activeItemId: string | null;
  scrollToItem: (id: string) => void;
  isEditing?: boolean;
};

export const createCustomSectionEntry = ({
  user,
  idea,
  section,
  activeItemId,
  scrollToItem,
  isEditing,
}: CustomSectionEntryProps): SideNavItemProps => {
  const canEdit = user ? util.canEditIdea(user, idea) : false;
  const sectionName = section.name ?? "";
  const submissibility =
    idea.submissibilityStatus?.detail?.[getSubmissibilitySectionKey(section)];
  const {
    required: missingRequiredFields,
    wordLimit: wordLimitIssues,
    custom: customIssues,
  } = groupIncorrectFields(submissibility);
  const tooltip = getSubmissibilitySummaryText(submissibility);
  const color = getSubmissibilityColor(submissibility);

  return {
    key: `${sectionName}-${section.id}`,
    type: "link",
    content: sectionName.charAt(0).toUpperCase() + sectionName.slice(1),
    tooltip:
      canEdit &&
      isEditing &&
      submissibility &&
      submissibility.visibleFields > 0 ? (
        <div>
          {tooltip}
          {customIssues.length > 0 ? (
            <>
              <Divider />
              <b>Issues:</b>
              <div style={{ maxHeight: 300, overflow: "auto", padding: 8 }}>
                <List variant="bulleted">
                  {customIssues?.map((field: SubmissibilityIncorrectField) => (
                    <ListItem key={field.id}>
                      {field.title}: {field.description}
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          ) : null}
          {wordLimitIssues.length > 0 ? (
            <>
              <Divider />
              <b>Exceeding word limit:</b>
              <div style={{ maxHeight: 300, overflow: "auto", padding: 8 }}>
                <List variant="bulleted">
                  {wordLimitIssues?.map(
                    (field: SubmissibilityIncorrectField) => (
                      <ListItem key={field.id}>{field.title}</ListItem>
                    ),
                  )}
                </List>
              </div>
            </>
          ) : null}
          {missingRequiredFields.length > 0 ? (
            <>
              <Divider />
              <b>
                Required{" "}
                {util.pluralise(
                  missingRequiredFields.length,
                  "field",
                  "fields",
                  false,
                )}{" "}
                remaining:
              </b>
              <div style={{ padding: 8, maxHeight: 300, overflow: "auto" }}>
                <List variant="bulleted">
                  {missingRequiredFields?.map(
                    (field: SubmissibilityIncorrectField) => (
                      <ListItem key={field.id}>
                        {field.title}
                        {field.description ? ` (${field.description})` : ""}
                      </ListItem>
                    ),
                  )}
                </List>
              </div>
            </>
          ) : null}
        </div>
      ) : null,
    iconLeft: CircleArrowRightIcon,
    iconRight:
      canEdit &&
      isEditing &&
      submissibility &&
      submissibility.visibleFields > 0 ? (
        <CircularProgress
          size={16}
          color={color}
          progress={getSubmissibilityProgress(submissibility)}
          strokeWidth={1.5}
          icon={<Icon icon={CheckIcon} style={{ color }} />}
          iconVisible={
            submissibility.requiredFields ===
              submissibility.completedRequiredFields &&
            (submissibility?.incorrectFields?.length || 0) === 0
          }
        />
      ) : undefined,
    active: activeItemId === section.id,
    onClick: () => scrollToItem(section.id),
  };
};
